/**
 * Activation tracking — the `board_customized` event (metrics-plan §1.6).
 *
 * Rules from the plan:
 *   - Fires ONCE per anon_id (a localStorage flag prevents re-firing).
 *   - Only fired on self-directed ops: add, remove, move, resize, configure.
 *   - Template selection does NOT count as activation (§1.6 edge-case rulings).
 *   - `ms_since_page_load` measures the gap from page load to the first op.
 *
 * The flag key is `snip_activated` (truthy string in localStorage).
 * The page-load timestamp is captured at module import time.
 */

export const ACTIVATION_FLAG_KEY = 'snip_activated';

/**
 * Millisecond timestamp for page load, anchored to `performance.timeOrigin`
 * (the navigation start time, which is more accurate than Date.now() at module
 * load time). Falls back to `Date.now()` in environments without the Performance
 * API (e.g. some jsdom test environments).
 *
 * metrics-plan §1.6 `ms_since_page_load`: this is the time from page load to
 * the first qualifying customization op (time-to-activation). Using
 * `performance.timeOrigin` gives us the navigation start, not the script
 * evaluation start, which is the correct zero-point for TTI measurements.
 */
const PAGE_LOAD_AT: number = (() => {
  try {
    if (typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number') {
      return performance.timeOrigin;
    }
  } catch { /* performance API unavailable */ }
  return Date.now();
})();

/** Returns true if the current anon_id has already been marked as activated. */
export function isActivated(): boolean {
  try {
    return localStorage.getItem(ACTIVATION_FLAG_KEY) !== null;
  } catch {
    return false;
  }
}

/** Persist the activation flag so `board_customized` never fires again for this anon_id. */
export function markActivated(): void {
  try {
    localStorage.setItem(ACTIVATION_FLAG_KEY, '1');
  } catch {
    // Storage unavailable — acceptable; the event may fire again on next visit,
    // which is tolerable per §1.6 ("at most once" is best-effort).
  }
}

/**
 * Milliseconds elapsed since the page loaded (navigation start), always ≥ 0.
 *
 * Uses `performance.now()` (which is relative to `performance.timeOrigin`) for
 * the current time when available — this is more accurate than `Date.now()`
 * because `performance.now()` is monotonic and not subject to clock skew.
 * Falls back to `Date.now() - PAGE_LOAD_AT` when Performance API is absent.
 */
export function msSincePageLoad(): number {
  try {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      // performance.now() is already relative to timeOrigin, so adding timeOrigin
      // gives us absolute ms since epoch — but we want ms since page load, which
      // is just performance.now() (it IS the elapsed time since navigation start).
      return Math.max(0, Math.round(performance.now()));
    }
  } catch { /* performance API unavailable */ }
  return Math.max(0, Date.now() - PAGE_LOAD_AT);
}
